import * as React from 'react';
import { useTheme } from '@emotion/react';
import { S } from '../styles';

export const PrototypeScene: React.FC<{
  animateConnections: boolean;
  live: number;
  handoff: number;
  completePicture: number;
  loadApp: number;
  download: number;
  downloadProgress: number;
  run: number;
  connection: number;
  ready: number;
}> = ({
  animateConnections,
  live,
  handoff,
  completePicture,
  loadApp,
  download,
  downloadProgress,
  run,
  connection,
  ready
}) => {
  const theme = useTheme();
  const colors = theme.featureGraph;
  const buildArrowY = 410 - 25 * completePicture;

  return (
    <g>
      <g opacity={download}>
        <rect
          x="-45"
          y="65"
          width="400"
          height="640"
          rx="24"
          fill={theme.surface.background}
          stroke={colors.interface.accent}
          strokeWidth="1.5"
        />
        <rect x="-45" y="65" width="400" height="640" rx="24" fill={colors.interface.background} />
        <text x="-10" y="105" fill={colors.interface.accent} fontSize="18" fontWeight="600">
          Container
        </text>
        <text x="-10" y="132" fill={theme.text.paragraph} fontSize="11">
          Install once on your device
        </text>
        <g opacity={run}>
          <rect
            x="-10"
            y="175"
            width="330"
            height="480"
            rx="18"
            fill={theme.surface.background}
            stroke={colors.runtime.border}
            strokeWidth="1.5"
          />
          <rect x="-10" y="175" width="330" height="480" rx="18" fill={colors.runtime.background} />
          <text x="20" y="211" fill={colors.runtime.accent} fontSize="16" fontWeight="600">
            Runtime Engine
          </text>
          <text x="20" y="236" fill={theme.text.paragraph} fontSize="10">
            Runs your app’s interface and logic
          </text>
        </g>
      </g>
      <g opacity={connection * (1 - handoff * (1 - completePicture))}>
        <path
          d={`M${-110 - 70 * completePicture} ${buildArrowY} H43`}
          fill="none"
          stroke={colors.interface.border}
          strokeWidth="2"
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset={1 - connection}
        />
        <S.Flow
          d={`M${-110 - 70 * completePicture} ${buildArrowY} H43`}
          running={animateConnections}
          fill="none"
          stroke={colors.interface.accent}
          strokeWidth="2"
          strokeDasharray="5 22"
          opacity={Math.max(0, (connection - 0.8) * 5)}
        />
        <path
          opacity={connection}
          d={`M36 ${buildArrowY - 5} L43 ${buildArrowY} L36 ${buildArrowY + 5}`}
          fill="none"
          stroke={colors.interface.accent}
          strokeWidth="2"
        />
        <text
          x={-33.5 - 79 * completePicture}
          y={buildArrowY - 20}
          textAnchor="middle"
          fill={colors.interface.accent}
          fontSize="10"
        >
          BUILD APP
        </text>
      </g>
      <text x="155" y="735" textAnchor="middle" fill={colors.runtime.accent} fontSize="12" opacity={ready}>
        Your app · ready to work offline
      </text>
      <g opacity={1 - handoff} textAnchor="middle" fontSize="12">
        <text x="155" y="246" fill={theme.text.heading} opacity={1 - live}>
          App concept
        </text>
        <text x="155" y="246" fill={colors.runtime.accent} opacity={live}>
          Built app
        </text>
      </g>
      <g opacity={download}>
        <path d="M490 385 H357" fill="none" stroke={colors.feature.border} strokeWidth="2" strokeLinejoin="round" />
        <S.Flow
          d="M490 385 H357"
          running={animateConnections}
          fill="none"
          stroke={colors.feature.accent}
          strokeWidth="2"
          strokeDasharray="5 22"
        />
        <path d="M365 379 L357 385 L365 391" fill="none" stroke={colors.feature.accent} strokeWidth="2" />
        <text x="422" y="350" textAnchor="middle" fill={colors.feature.accent} fontSize="10">
          DOWNLOAD
        </text>
        <text x="422" y="367" textAnchor="middle" fill={colors.feature.accent} fontSize="10">
          CONTAINER
        </text>
        <g transform="translate(490 265)">
          <rect
            width="250"
            height="240"
            rx="18"
            fill={theme.surface.background}
            stroke={colors.feature.border}
            strokeWidth="1.5"
          />
          <rect width="250" height="240" rx="18" fill={colors.feature.background} />
          <circle cx="125" cy="39" r="13" fill="none" stroke={colors.feature.accent} strokeWidth="2" />
          <path
            d="M101 76 V70 Q101 55 125 55 Q149 55 149 70 V76"
            fill="none"
            stroke={colors.feature.accent}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <text x="125" y="109" textAnchor="middle" fill={theme.text.heading} fontSize="22" fontWeight="600">
            App user
          </text>
          <text x="125" y="137" textAnchor="middle" fill={theme.text.paragraph} fontSize="12">
            Downloads the Container
          </text>
          <g opacity={1 - ready}>
            <g
              transform={`translate(125 ${160 + Math.sin(downloadProgress * Math.PI * 6) * 4 * (1 - downloadProgress)})`}
            >
              <path
                d="M0 -10 V5 M-6 -1 L0 5 L6 -1 M-10 7 V12 H10 V7"
                fill="none"
                stroke={colors.feature.accent}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <path d="M35 192 H215" stroke={colors.feature.border} strokeWidth="4" strokeLinecap="round" />
            <path
              d="M35 192 H215"
              stroke={colors.feature.accent}
              strokeWidth="4"
              strokeLinecap="round"
              pathLength="1"
              strokeDasharray="1"
              strokeDashoffset={1 - downloadProgress}
              opacity={downloadProgress > 0 ? 1 : 0}
            />
            <text x="125" y="218" textAnchor="middle" fill={theme.text.paragraph} fontSize="11">
              {downloadProgress < 1 ? 'Downloading…' : 'Container installed'}
            </text>
          </g>
          <text x="125" y="192" textAnchor="middle" fill={colors.feature.accent} fontSize="12" opacity={ready}>
            Opens and runs your app
          </text>
        </g>
      </g>
      <g opacity={run * (1 - loadApp)}>
        <rect
          x="45"
          y="265"
          width="220"
          height="350"
          rx="30"
          fill="none"
          stroke={colors.runtime.border}
          strokeWidth="1.5"
          strokeDasharray="6 8"
        />
        <text x="155" y="425" textAnchor="middle" fill={colors.runtime.accent} fontSize="14">
          Ready to load your app
        </text>
      </g>
    </g>
  );
};
